
import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

interface ImageUploadComponentProps {
  label?: string;
  value: string;
  onChange: (info: {
    dataUrl: string;
    base64Content: string;
    file: File;
    name: string;
    mimeType: string;
    size: number;
    width?: number;
    height?: number;
  } | undefined) => void;
}

const ImageUploadComponent: React.FC<ImageUploadComponentProps> = ({ label = 'Thumbnail', value, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => {
        if (ev.target && typeof ev.target.result === 'string') {
          const dataUrl = ev.target.result;
          const base64 = dataUrl.split(',')[1] || '';
          const img = new window.Image();
          img.onload = () => {
            onChange({
              dataUrl,
              base64Content: base64,
              file,
              name: file.name,
              mimeType: file.type,
              size: file.size,
              width: img.width,
              height: img.height,
            });
          };
          img.src = dataUrl;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <FormControl variant="outlined" sx={{ mt: 0 }}>
      <Box sx={{
        position: 'relative',
        border: '1.5px solid #bdbdbd',
        borderRadius: 2,
        p: 2,
        pt: 3,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}>
        <FormLabel
          component="legend"
          sx={{
            position: 'absolute',
            top: 0,
            left: 12,
            transform: 'translateY(-50%)',
            bgcolor: '#ffffff',
            px: 0.5,
            fontSize: 13,
            color: 'text.secondary',
            zIndex: 1,
          }}
        >
          {label}
        </FormLabel>
        <Box sx={{
          width: 120,
          height: 120,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {value ? (
            <img src={value} alt="Thumbnail" style={{ maxWidth: '100%', maxHeight: '100%' }} />
          ) : (
            <img src="https://via.placeholder.com/120?text=No+Image" alt="No Thumbnail" style={{ maxWidth: '100%', maxHeight: '100%', opacity: 0.5 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 0.5, minWidth: 120 }}>
          <Button
            variant="outlined"
            component="span"
            onClick={() => fileInputRef.current?.click()}
            sx={{ width: 120 }}
          >
            {value ? 'Replace' : 'Upload'}
          </Button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {value && (
            <Button
              variant="outlined"
              color="error"
              sx={{ mt: 1, width: 120 }}
              onClick={() => onChange(undefined)}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>
    </FormControl>
  );
};

export default ImageUploadComponent;
